import {
  DEFAULT_PRIMARY_COMPANIES,
  TRIAL_ALLOWED_COMPANY_RCIDS,
} from '../contexts/defaultFilters';
import { getNumericIdFromNestedId } from '../utils/get-numeric-id-from-nested-id';

/** Membership by bare rcid, the one place the allow-list set is read. */
export const isTrialAllowedRcid = (rcid: string): boolean =>
  TRIAL_ALLOWED_COMPANY_RCIDS.has(rcid);

/**
 * Tree node ids are ancestor-path compounds (`8_257_919809`) whose trailing
 * segment is the rcid. Matching on that segment keeps a node reached by a
 * different path — a search result vs a drilled branch — matching the same
 * company.
 */
const isTrialAllowedCompanyId = (id: string): boolean => {
  const rcid = getNumericIdFromNestedId(id);
  return rcid !== null && isTrialAllowedRcid(String(rcid));
};

/**
 * The company picker's submit rule for trial users. Only company-level
 * selections are checked, mirroring `restrictCompaniesForTrial`: the
 * entitlement is on named companies, not on breadth of analysis, so the
 * rics_k10 / rics_k400 rollups this same tree offers stay selectable.
 *
 * `levelIdOf` comes from the caller because an id's level is a property of the
 * filter it belongs to (`selectionListIds` indexed by depth), not of the id.
 * An empty selection is vacuously submittable — the picker disables submit on
 * that separately, under its own copy.
 */
export const areTrialAllowedSelections = (
  ids: string[],
  levelIdOf: (id: string) => string | undefined
): boolean =>
  ids.every((id) => levelIdOf(id) !== 'company' || isTrialAllowedCompanyId(id));

const listNames = (names: string[]): string =>
  names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

/**
 * Why Update is disabled, named from `DEFAULT_PRIMARY_COMPANIES` — which IS the
 * trial entitlement today, see `TRIAL_ALLOWED_COMPANY_RCIDS` — so the copy
 * cannot drift from the list the picker offers.
 *
 * Plain text: `SelectionListControls` renders it as a tooltip label and a
 * `title`, neither of which can carry the upgrade link the toasts use.
 */
export const TRIAL_COMPANY_SUBMIT_TOOLTIP = `Your trial includes ${listNames(
  DEFAULT_PRIMARY_COMPANIES.map((selection) => selection.name)
)}. Deselect the others to update.`;
